import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Flag, ArrowUp, ArrowDown } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { affiliateService } from '@/services/affiliateService';

export const AffiliateAdminDashboard = () => {
  const { data: affiliateStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminAffiliateStats'],
    queryFn: () => affiliateService.getAdminAffiliateStats(),
  });

  if (statsLoading) {
    return <div className="p-8">Loading dashboard data...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Affiliate Administration</h1>
        <p className="text-muted-foreground mt-1">Overview of affiliate program performance</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Affiliates</CardDescription>
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl">{affiliateStats?.totalAffiliates || 0}</CardTitle>
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm">
              <span className={affiliateStats?.affiliateGrowth && affiliateStats.affiliateGrowth > 0 ? 'text-green-500' : 'text-red-500'}>
                {affiliateStats?.affiliateGrowth && affiliateStats.affiliateGrowth > 0 ? <ArrowUp className="inline h-3 w-3 mr-1" /> : <ArrowDown className="inline h-3 w-3 mr-1" />}
                {Math.abs(affiliateStats?.affiliateGrowth || 0)}%
              </span>
              <span className="text-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Affiliates</CardDescription>
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl">{affiliateStats?.activeAffiliates || 0}</CardTitle>
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {((affiliateStats?.activeAffiliates || 0) / (affiliateStats?.totalAffiliates || 1) * 100).toFixed(1)}% of total affiliates
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Referrals</CardDescription>
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl">{affiliateStats?.totalReferrals || 0}</CardTitle>
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm">
              <span className={affiliateStats?.referralGrowth && affiliateStats.referralGrowth > 0 ? 'text-green-500' : 'text-red-500'}>
                {affiliateStats?.referralGrowth && affiliateStats.referralGrowth > 0 ? <ArrowUp className="inline h-3 w-3 mr-1" /> : <ArrowDown className="inline h-3 w-3 mr-1" />}
                {Math.abs(affiliateStats?.referralGrowth || 0)}%
              </span>
              <span className="text-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Flagged Activity</CardDescription>
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl">{affiliateStats?.flaggedActivity || 0}</CardTitle>
              <Flag className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Requires review
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Pending Applications</CardTitle>
            <CardDescription>Affiliates awaiting approval</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {affiliateStats?.pendingApplications && affiliateStats.pendingApplications.length > 0 ? (
                <div className="divide-y divide-border">
                  {affiliateStats.pendingApplications.map((application, index) => (
                    <div key={index} className="py-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{application.name}</p>
                        <p className="text-sm text-muted-foreground">{application.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <button className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90">
                          Approve
                        </button>
                        <button className="text-xs px-2 py-1 bg-destructive text-destructive-foreground rounded hover:bg-destructive/90">
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No pending applications
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Referrals</CardTitle>
            <CardDescription>Latest user sign-ups from affiliates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {affiliateStats?.recentReferrals && affiliateStats.recentReferrals.length > 0 ? (
                <div className="divide-y divide-border">
                  {affiliateStats.recentReferrals.map((referral, index) => (
                    <div key={index} className="py-3">
                      <div className="flex justify-between">
                        <p className="font-medium">{referral.affiliateName}</p>
                        <p className="text-sm text-muted-foreground">{new Date(referral.date).toLocaleDateString()}</p>
                      </div>
                      <div className="flex justify-between mt-1">
                        <p className="text-sm text-muted-foreground">User: {referral.user}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          referral.status === 'converted' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {referral.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No recent referrals
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateAdminDashboard;
